import { Link } from '@tanstack/react-router';
import { ChevronRight, Clock } from 'lucide-react';
import { SUBJECTS } from '@jeraisonne/db';
import { cn, MathText } from '@jeraisonne/ui';
import { HandGrade, PaperSheetCard } from '@jeraisonne/ui/paper';
import { AvatarCircle } from '@/components/avatar-circle';
import { formatRelativeDate, formatDuration } from './format';
import { studentLinkFor } from './attempt-links';
import { scoreAppreciation, scoreTone, stepBarClass } from './attempt-score';
import type { AttemptRow, ViewerRole } from './types';

const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

/** État d'un essai dans le Feed : terminé, en cours ou abandonné. Pure. */
export type AttemptFeedState = 'completed' | 'in_progress' | 'abandoned';

export function attemptFeedState(status: AttemptRow['status']): AttemptFeedState {
  return status === 'in_progress' || status === 'abandoned' ? status : 'completed';
}

/**
 * En-tête : « Tu as terminé… » quand c'est l'élève connecté lui-même ;
 * « … a commencé / abandonné un exercice » pour un essai non terminé (Feed
 * admin). Pure.
 */
export function attemptHeadline(
  authorName: string,
  viewerId: string | null | undefined,
  studentId: string,
  state: AttemptFeedState | boolean = 'completed',
): { name: string; verb: string } {
  const s: AttemptFeedState =
    state === true ? 'in_progress' : state === false ? 'completed' : state;
  const action = s === 'in_progress' ? 'commencé' : s === 'abandoned' ? 'abandonné' : 'terminé';
  if (viewerId && viewerId === studentId) return { name: 'Tu', verb: ` as ${action} un exercice` };
  return { name: authorName, verb: ` a ${action} un exercice` };
}

/**
 * Progression d'un essai en cours : questions répondues / questions de
 * l'exercice (le total peut manquer : on ne montre alors que les réponses). Pure.
 */
export function inProgressSummary(answered: number, total: number | null | undefined): string {
  const plural = (n: number) => (n > 1 ? 's' : '');
  if (total && total > 0) {
    return `${answered}/${total} question${plural(total)} répondue${plural(total)}`;
  }
  if (answered === 0) return 'Aucune réponse pour l’instant';
  return `${answered} question${plural(answered)} répondue${plural(answered)}`;
}

/**
 * Carte « X a terminé un exercice » du Feed, posée sur du papier de brouillon
 * (elle se distingue du reste du Feed) : grand avatar, titre (maths rendues),
 * note écrite à la main comme par le prof, une barre par question.
 * L'en-tête ouvre la fiche de l'élève (selon le rôle), le corps les résultats.
 */
export function AttemptCard({
  data,
  authorName,
  authorAvatar,
  viewerRole,
  viewerId,
}: {
  data: AttemptRow;
  authorName: string;
  authorAvatar: string | null | undefined;
  viewerRole: ViewerRole;
  viewerId?: string | null;
}) {
  const steps = data.attempt_steps ?? [];
  const state = attemptFeedState(data.status);
  // Non terminé (en cours OU abandonné) : progression au lieu du score.
  const inProgress = state !== 'completed';
  const abandoned = state === 'abandoned';
  const correctCount = steps.filter((s) => s.was_correct === true).length;
  const successRate =
    steps.length > 0
      ? Math.round((correctCount / steps.length) * 100)
      : data.score !== null
        ? Math.round(data.score)
        : null;
  const totalMs = steps.reduce((sum, s) => sum + (s.time_ms ?? 0), 0);
  const chapter = data.exercises?.chapters;
  const studentLink = studentLinkFor(viewerRole, viewerId, data.student_id);
  const headline = attemptHeadline(authorName, viewerId, data.student_id, state);
  const tone = scoreTone(inProgress ? null : successRate);
  const title = data.exercises?.title ?? 'Exercice';
  const date = abandoned
    ? (data.abandoned_at ?? data.started_at)
    : inProgress
      ? data.started_at
      : data.completed_at;
  // En cours : une barre grise par question pas encore répondue.
  const remaining = inProgress
    ? Math.max(0, (data.question_count ?? steps.length) - steps.length)
    : 0;

  const identity = (
    <>
      <AvatarCircle
        name={authorName}
        src={authorAvatar}
        size="lg"
        className="ocean:ring-white/10 shrink-0 ring-2 ring-white"
      />
      <div className="min-w-0 flex-1 leading-tight">
        <p className="font-sans text-sm">
          <span className="text-ink-900 font-semibold">{headline.name}</span>
          <span className="text-ink-500">{headline.verb}</span>
        </p>
        <p className="text-ink-400 mt-0.5 font-sans text-xs">
          {date ? formatRelativeDate(date) : ''}
        </p>
      </div>
    </>
  );

  return (
    <PaperSheetCard className="overflow-hidden rounded-2xl">
      <div className="pl-7 pr-4 pt-4">
        {studentLink ? (
          <Link
            {...studentLink}
            aria-label={`Voir ${authorName}`}
            className="-m-1 flex min-h-11 items-center gap-3 rounded-xl p-1 hover:opacity-90"
          >
            {identity}
          </Link>
        ) : (
          <div className="flex min-h-11 items-center gap-3">{identity}</div>
        )}
      </div>

      <Link
        to="/attempts/$attemptId"
        params={{ attemptId: data.id }}
        aria-label={
          abandoned
            ? `Voir l’essai abandonné sur ${title}`
            : inProgress
              ? `Voir la progression sur ${title}`
              : `Voir les résultats de ${title}`
        }
        className="block pb-4 pl-7 pr-4 pt-3 transition-transform active:scale-[0.99]"
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-ink-900 font-display text-base font-bold leading-snug">
              <MathText>{title}</MathText>
            </p>
            <p className="text-ink-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-sans text-xs">
              {chapter && <span>{SUBJECT_LABEL[chapter.subject] ?? chapter.subject}</span>}
              {inProgress && <span>{inProgressSummary(steps.length, data.question_count)}</span>}
              {!inProgress && steps.length > 0 && (
                <span>
                  {correctCount}/{steps.length} bonnes réponses
                </span>
              )}
              {totalMs > 0 && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" aria-hidden /> {formatDuration(totalMs)}
                </span>
              )}
            </p>
          </div>
          {abandoned ? (
            <span className="bg-ink-100 text-ink-600 ocean:bg-white/10 ocean:text-ink-300 shrink-0 rounded-full px-3 py-1 font-sans text-xs font-semibold">
              Abandonné
            </span>
          ) : inProgress ? (
            <span className="bg-primary-50 text-primary-700 ocean:bg-primary-500/15 ocean:text-primary-200 shrink-0 rounded-full px-3 py-1 font-sans text-xs font-semibold">
              En cours
            </span>
          ) : (
            successRate !== null && <HandGrade value={successRate} className="-mt-1 shrink-0" />
          )}
        </div>

        {steps.length + remaining > 0 && (
          <div className="mt-3 flex gap-1" aria-hidden>
            {steps.map((s, i) => (
              <span
                key={i}
                className={cn('h-1.5 flex-1 rounded-full', stepBarClass(s.was_correct))}
              />
            ))}
            {Array.from({ length: remaining }, (_, i) => (
              <span
                key={`r${i}`}
                className="bg-ink-100 ocean:bg-white/10 h-1.5 flex-1 rounded-full"
              />
            ))}
          </div>
        )}

        <div className="mt-2.5 flex items-center justify-between gap-2 font-sans text-xs">
          <span className={cn('font-semibold', tone.text)}>
            {inProgress
              ? steps.length > 0
                ? `${correctCount} bonne${correctCount > 1 ? 's' : ''} réponse${correctCount > 1 ? 's' : ''}`
                : ''
              : scoreAppreciation(successRate)}
          </span>
          <span className="text-primary-600 ocean:text-primary-300 flex items-center gap-0.5 font-semibold">
            {abandoned ? 'Voir l’essai' : inProgress ? 'Voir sa progression' : 'Voir le détail'}{' '}
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </span>
        </div>
      </Link>
    </PaperSheetCard>
  );
}
